import { APICfg } from 'services/api/api'
import {
  parseTimeBeforeAfterHint,
  parseQuantityHint,
  writeQuantityHint,
  writeTimeBeforeAfterHint,
} from 'ui-shared/Hints/common'
import { mainProductParse, mainProductWrite } from '../mainProduct'
import { APIPricingRuleSet } from '../types'
import { ProfessionalFigureToAPI } from './types/api/request-models'
import { APIProfessionalFigure } from './types/api/response-models'
import {
  ProfessionalFigure,
  ProfessionalPricingRuleSet,
} from './types/internal/professional-models'

// ============
// PARSE
// ============

export const professionalFigureParse = ({
  pricing,
  ...rest
}: APIProfessionalFigure): ProfessionalFigure => {
  return {
    ...mainProductParse(rest),
    section: rest.sections[0]?.id,
    sectionName: rest.sections[0]?.name,
    ...parseTimeBeforeAfterHint(rest),
    ...parseQuantityHint(rest),
    pricing: pricing.map(parseProfessionalPricing),
  }
}

const parseProfessionalPricing = ({
  timePeriodRules,
  minHoursRules,
  ...rest
}: APIPricingRuleSet): ProfessionalPricingRuleSet => ({
  ...rest,
  // even if API contract supports an array, professional pricing just supports one minHoursRule
  cost: minHoursRules[0].cost,
  price: minHoursRules[0].price,
  shiftPricingRules: timePeriodRules.map((e) => ({ ...e, mode: 'tpr' })),
})

// ============
// WRITE
// ============

const professionalWrite = async (
  req: ProfessionalFigure
): Promise<ProfessionalFigureToAPI> => {
  const {
    section,
    sectionName,
    defaultTimeBeforeAfterHint,
    timeBeforeAfterHint,
    showTimeBeforeAfterHint,
    pricing,
    ...rest
  } = req

  const sections = section ? [section] : []
  const base = await mainProductWrite({
    ...rest,
  })

  return {
    ...base,
    sections,
    quantityHint: writeQuantityHint(req),
    timeBeforeAfterHint: writeTimeBeforeAfterHint(req),
    pricing: pricing.map(writeProfessionalPricing),
  }
}

const writeProfessionalPricing = ({
  cost,
  price,
  shiftPricingRules,
  ...rest
}: ProfessionalPricingRuleSet): APIPricingRuleSet => ({
  ...rest,
  // professional pricing uses a wider API contract (see https://gitlab.com/kampaay/general/-/wikis/ByRules-Pricing)
  // but we narrow it down with hardcoded values
  minHoursRules: [
    {
      cost,
      price,
      atLeast: 0.5,
      isExtra: false,
      priceType: 'proportional',
      name: undefined,
    },
  ],
  timePeriodRules:
    shiftPricingRules?.map((rule) => ({
      ...rule,
      isExtra: false,
      priceType: 'proportional',
    })) ?? [],
})

// ============
// CFG
// ============

const apiCfg: APICfg = {
  endpoint: 'professionalfigure',
  parse: professionalFigureParse,
  write: professionalWrite,
}

export default apiCfg
